import clsx from "clsx";
import {
  FILE_TABLE_ACTION_UPLOAD,
  FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
  FILE_TABLE_ACTION_UPLOAD_HIS,
  TRACK_EVENTS,
} from "core/consts";
import {
  ALLOWED_FILE_MIME_TYPES,
  MAX_FILE_SIZE_AHB_MB,
  MAX_FILE_SIZE_MB,
} from "core/model/files/config";
import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "ds/ui";

import { useFileUpload } from "ds_legacy/components/FileUploader";
import { SetAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { useIsEligiblePage } from "dsl/ecosystems/PatientFiles/useUploadFile";
import { useIsScrollable } from "dsl/hooks";
import { ArrowLeftIcon, FilesIcon, UploadIcon } from "lucide-react";
import { useTracking } from "react-tracking";
import { useLoggedCareseekerConfig } from "reduxentities/hooks";
import { useTranslations } from "translations";

export function DragNDropScreen({
  handleChange,
  setAction,
}: {
  handleChange: (file?: File) => void;
  setAction: SetAction<FileFeatureData>;
}) {
  const isEligibility = useIsEligiblePage();
  const translations = useTranslations();
  const { handleClick, inputRef, processedFile } = useFileUpload();
  const { trackEvent } = useTracking();
  const { isScrollable, ref: modalBodyRef } = useIsScrollable<HTMLDivElement>();
  const hasActiveOnPrem = useLoggedCareseekerConfig(
    "on_premise_archive_integration_enabled",
  );

  const fileInfoTexts = [
    translations.fileSection.dragAndDrop.maxFileSize({
      maxSize: (isEligibility
        ? MAX_FILE_SIZE_AHB_MB
        : MAX_FILE_SIZE_MB
      ).toFixed(1),
    }),
    translations.fileUploader.newModal.maximumNumberOfFiles,
    translations.fileSection.dragAndDrop.disclaimerFileType,
  ];

  const renderFileInfoWithSeparators = () => {
    return fileInfoTexts.map((text, index) => (
      <span key={index} className="inline-flex items-center">
        {index > 0 && <span className="mx-2 shrink-0 select-none">|</span>}
        <span className="whitespace-nowrap">{text}</span>
      </span>
    ));
  };

  return (
    <ModalContent>
      <ModalHeader
        as="h1"
        className="border-b border-divider pb-4 !text-heading-xl font-bold"
        id="file-upload-modal-drag-n-drop-title"
      >
        {translations.fileSection.dragAndDrop.title}
      </ModalHeader>
      <ModalBody className="!p-6">
        <div
          ref={modalBodyRef}
          aria-label={
            translations.fileSection.dragAndDrop.instructionDragAndDrop
          }
          className="flex flex-col items-center justify-around gap-6 rounded-md border-2 border-dashed border-primary bg-primary-light p-9"
          role="button"
          tabIndex={0}
          onDragOver={(e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e: React.DragEvent) => {
            trackEvent({
              file_implementation: "HIS",
              file_type: processedFile?.file.type,
              name: TRACK_EVENTS.UPLOAD_FILE_FROM_COMPUTER_DRAG_AND_DROP,
            });
            e.preventDefault();
            e.stopPropagation();
            handleChange(e.dataTransfer.files?.[0]);
            setAction({
              data: processedFile || ({} as FileFeatureData),
              type: FILE_TABLE_ACTION_UPLOAD,
            });
            if (!processedFile) {
              setAction({
                data: {} as FileFeatureData,
                type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
              });
            }
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClick();
            }
          }}
        >
          <div className="flex flex-col items-center gap-6">
            <UploadIcon className="text-primary-dark" size={24} />
            <p className="font-semibold text-primary-dark">
              {translations.fileUploader.newModal.dragDropInstruction}
            </p>
          </div>
          <p className="text-sm font-semibold text-primary-dark">
            {
              translations.fileSection.dragAndDrop
                .selectBetweenDragAndDropOptions
            }
          </p>
          <div className="flex flex-col items-center">
            <input
              ref={inputRef}
              accept={ALLOWED_FILE_MIME_TYPES.join(",")}
              className="hidden"
              name="name"
              style={{
                visibility: "hidden",
              }}
              type="file"
              onChange={(event) => handleChange(event.target.files?.[0])}
            />
            <Button
              color="primary"
              id="select"
              size="lg"
              startContent={<FilesIcon />}
              variant="solid"
              onPress={() => {
                trackEvent({
                  file_implementation: "HIS",
                  name: TRACK_EVENTS.UPLOAD_FILE_FROM_COMPUTER_SELECT_CLICKED,
                });
                handleClick();
              }}
            >
              {translations.fileUploader.newModal.selectFiles}
            </Button>
          </div>
          <div className="flex flex-col items-center">
            <p className="flex flex-row flex-wrap items-center justify-center gap-x-2 text-sm text-primary-dark">
              {renderFileInfoWithSeparators()}
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter
        className={clsx("flex w-full !p-6", {
          "!justify-between": hasActiveOnPrem,
          "relative !shadow-2xl": isScrollable,
        })}
      >
        {hasActiveOnPrem && (
          <Button
            color="primary"
            id="file-upload-modal-drag-n-drop-back"
            size="lg"
            startContent={<ArrowLeftIcon />}
            variant="ghost"
            onPress={() => {
              setAction({
                data: {} as FileFeatureData,
                type: FILE_TABLE_ACTION_UPLOAD_HIS,
              });
            }}
          >
            {translations.actions.back}
          </Button>
        )}
        <Button
          color="primary"
          id="file-upload-modal-drag-n-drop-upload"
          isDisabled
          size="lg"
          startContent={<UploadIcon />}
        >
          {translations.actions.uploadFile}
        </Button>
      </ModalFooter>
    </ModalContent>
  );
}
